// src/registered/site-admin/report_component_library/ReportComponentDTO.js
//
// Report Component Library — domain model.
//
// A "report component" is a reusable, typed building block. It is defined once
// here and then composed by a report template into its sections (Report ->
// Pillar -> Section -> Component instance). A component declares:
//   - how it presents  (renderType)
//   - what a person fills in when it is used  (slots)
//   - what taxonomy binding it accepts        (bindingCapability)

export const RENDER_TYPES = [
  // Original presentation styles
  { code: "NARRATIVE", label: "Narrative block" },
  { code: "METRIC_TABLE", label: "Metric / KPI table" },
  { code: "CHART", label: "Chart" },
  { code: "TARGET_VS_ACTUAL", label: "Target vs actual" },
  { code: "DISCLOSURE_REQUIREMENT", label: "Disclosure requirement" },
  { code: "EVIDENCE_LIST", label: "Evidence / attachment list" },
  // Value shapes mirrored from the XBRL datapoint types we bind to. Frontend
  // only for now — not yet in Ref.ESG_ComponentRenderType.
  { code: "MONETARY_VALUE", label: "Monetary value" },
  { code: "NUMERIC_VALUE", label: "Numeric value" },
  { code: "PERCENTAGE", label: "Percentage" },
  { code: "BOOLEAN", label: "Yes / No" },
  { code: "DATE", label: "Date" },
  { code: "ENUMERATION", label: "Choice (enumeration)" },
  { code: "TEXT", label: "Text" },
];

/**
 * Maps a bound disclosure's classified kind (see classifyKind in the controller)
 * onto a render type, so adding a disclosure can adopt its presentation
 * automatically. Kinds with no confident mapping ("Value", "Other") are absent.
 */
export const KIND_TO_RENDER_TYPE = {
  "Narrative (text block)": "NARRATIVE",
  Monetary: "MONETARY_VALUE",
  Numeric: "NUMERIC_VALUE",
  Percentage: "PERCENTAGE",
  "Yes / No": "BOOLEAN",
  Date: "DATE",
  "Choice (enumeration)": "ENUMERATION",
  Text: "TEXT",
};

export const SLOT_VALUE_TYPES = [
  "number",
  "boolean",
  "enum",
  "date",
  "text",
  "richtext",
];

export const BINDING_CARDINALITY = ["ONE", "MANY"];

let _seq = 0;
export const newLocalId = (prefix = "rc") => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  _seq += 1;
  return `${prefix}_${Date.now()}_${_seq}`;
};

export class ReportComponentSlotOptionDTO {
  constructor({ id = null, optionLabel = "", optionValue = "", sortOrder = 0 } = {}) {
    this.id = id || newLocalId("opt");
    this.optionLabel = optionLabel;
    this.optionValue = optionValue;
    this.sortOrder = sortOrder;
  }
}

export class ReportComponentSlotDTO {
  constructor({
    id = null,
    slotKey = "",
    label = "",
    valueType = "text",
    isRequired = false,
    unitFamily = "",
    guidance = "",
    sortOrder = 0,
    options = [],
  } = {}) {
    this.id = id || newLocalId("slot");
    this.slotKey = slotKey;
    this.label = label;
    this.valueType = SLOT_VALUE_TYPES.includes(valueType) ? valueType : "text";
    this.isRequired = !!isRequired;
    this.unitFamily = unitFamily;
    this.guidance = guidance;
    this.sortOrder = sortOrder;
    this.options = (Array.isArray(options) ? options : []).map(
      (o) => new ReportComponentSlotOptionDTO(o),
    );
  }
}

export class ReportComponentBindingCapabilityDTO {
  constructor({
    cardinality = "MANY",
    isMandatory = false,
    allowedDataTypeFamily = "",
  } = {}) {
    this.cardinality = BINDING_CARDINALITY.includes(cardinality)
      ? cardinality
      : "MANY";
    this.isMandatory = !!isMandatory;
    this.allowedDataTypeFamily = allowedDataTypeFamily;
  }
}

/**
 * One disclosure this component reports — a concrete binding to a taxonomy
 * concept (Ref.ESG_TaxonomyDetails). `rowId` null means a manually typed
 * disclosure (no taxonomy loaded / not found).
 *
 * Each disclosure carries its OWN presentation: label, render type and
 * guidance. A component that reports N disclosures has N of these blocks.
 */
export class ReportComponentDisclosureDTO {
  constructor({
    id = null,
    rowId = null,
    xbrlTag = "",
    measureName = "",
    headerId = null,
    label = "",
    renderType = "",
    guidance = "",
  } = {}) {
    // Own GUID, minted when the item is selected — the instance identity used
    // for edit / remove / ordering. Distinct from `rowId`, which is the
    // taxonomy datapoint this instance points at.
    this.id = id || newLocalId("disc");
    this.rowId = rowId != null && rowId !== "" ? String(rowId) : null;
    this.xbrlTag = xbrlTag || "";
    this.measureName = measureName || "";
    this.headerId = headerId != null && headerId !== "" ? String(headerId) : null;
    this.label = label || "";
    this.renderType = RENDER_TYPES.some((r) => r.code === renderType)
      ? renderType
      : "";
    this.guidance = guidance || "";
  }

  /** Taxonomy reference — used to stop the same datapoint being bound twice. */
  get taxRef() {
    return this.rowId || `free:${this.measureName}`;
  }
}

export default class ReportComponentDTO {
  constructor({
    id = null,
    label = "",
    categoryTag = "",
    boundDisclosures = [],
    renderType = "",
    metaDescription = "",
    slots = [],
    bindingCapability = {},
    isActive = true,
    version = 1,
    createdBy = "",
    createdAt = null,
    updatedAt = null,
  } = {}) {
    this.id = id || newLocalId("rc");
    this.label = label;
    this.categoryTag = categoryTag; // legacy free-text grouping — no longer edited
    this.boundDisclosures = (Array.isArray(boundDisclosures) ? boundDisclosures : [])
      .map((d) => new ReportComponentDisclosureDTO(d))
      .filter((d) => d.measureName || d.xbrlTag || d.rowId);
    this.renderType = RENDER_TYPES.some((r) => r.code === renderType)
      ? renderType
      : ""; // no default — the author must choose
    this.metaDescription = metaDescription;
    this.slots = (Array.isArray(slots) ? slots : [])
      .map((s, i) => new ReportComponentSlotDTO({ ...s, sortOrder: s?.sortOrder ?? i }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
    this.bindingCapability = new ReportComponentBindingCapabilityDTO(
      bindingCapability || {},
    );
    this.isActive = isActive !== false;
    this.version = Number.isFinite(version) && version > 0 ? version : 1;
    this.createdBy = createdBy;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
  }

  /** Compact row for the library index / list view. */
  toSummary() {
    const first = this.boundDisclosures[0];
    return {
      id: this.id,
      label:
        this.label ||
        this.boundDisclosures.map((d) => d.label).filter(Boolean).join(" · ") ||
        first?.measureName ||
        "(unnamed component)",
      categoryTag: this.categoryTag,
      disclosureCount: this.boundDisclosures.length,
      primaryDisclosure: first?.measureName || "",
      renderType: first?.renderType || this.renderType,
      slotCount: this.slots.length,
      isActive: this.isActive,
      version: this.version,
      updatedAt: this.updatedAt,
    };
  }
}
